<script>
  // Step 3 body: Build Frames. Renders inside a <StepCard>; the parent
  // panel owns the card's number/title/subline/done/warning.
  import { createEventDispatcher, getContext } from "svelte";

  import { Modal } from "svelte-utilities";
  import analyzeOpeningClips from "#src/extensions/analyze-opening-clips.js";
  import { singleOpeningLlmModel, singleOpeningReasoningLevel } from "#src/stores/ui.js";
  import { alerts } from "#src/stores/alerts.js";
  import formatDims from "@local/extensions/formatters/format-dims.js";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import { isFrameBuilt } from "../derive.js";
  import { builtLabel, plural } from "../labels.js";
  import { overlayOversize } from "../oversize.js";
  import ScaleWarning from "../ScaleWarning.svelte";

  export let group;
  /** Every frame location in the working set, derived once by FrameXPanel. */
  export let frames;
  /** Count of frames that are built — have a width and height. */
  export let builtCount;
  export let visibleIndices;
  /** Both only for the scale warning — this step reads frames, not pages. */
  export let pages;
  export let selectedPageList;
  export let selectedLocationList;
  export let spinners;

  const dispatch = createEventDispatcher();
  const org = getContext("org");
  const fileData = getContext("fileData");

  let analyzing = false;
  let scopeModal;
  let scopeChoice = "all";

  // The right panel already reviews frames properly — grouped, sortable,
  // selectable. This list starts closed and opens for exactly one reason: a
  // build just finished, which is the moment there is new output to check. Not
  // a selection, not step 3 becoming current. Once open it is the user's: a
  // collapse sticks until they reopen it, or until the next build produces
  // something new to look at. Deliberately not persisted across a mount.
  let reviewOpen = false;

  // "Not yet built" means no dimensions — the same definition of built used
  // everywhere else. A frame with a hand-entered width and height counts as
  // built and is skipped by the "only unbuilt" Build scope.
  $: unbuiltFrames = frames.filter((loc) => !isFrameBuilt($group.items[loc.record_id]));

  $: selectedFrameIds = new Set(selectedLocationList.map((l) => l.id));
  $: selectedFrames = frames.filter((loc) => selectedFrameIds.has(loc.id));

  $: frameCount = frames.length;
  $: unbuiltCount = unbuiltFrames.length;
  $: selectedCount = selectedFrames.length;

  // Overlays that outgrew their boundary — the telltale of a wrong Frame
  // Scale. Size-based only (oversize.js), so dragging never flickers it.
  $: oversizedCount = frames.filter((loc) => overlayOversize($group.items[loc.record_id])).length;

  function scopeFrames(choice) {
    if (choice === "selected") return selectedFrames;
    if (choice === "unbuilt") return unbuiltFrames;
    return frames;
  }

  function openScopeDialog(preset = null) {
    // The one button states no scope of its own, so it opens with no preset
    // and this picks the narrowest scope that still has something in it —
    // your selection, else the unbuilt remainder, else everything (a rebuild).
    // Resolved when the dialog opens rather than tracked reactively: a
    // selection made while it's open must not move the radio out from under
    // the user. `preset` is kept for a caller that does know its own scope.
    scopeChoice =
      preset ??
      (selectedCount > 0 ? "selected" : unbuiltCount > 0 && unbuiltCount < frameCount ? "unbuilt" : "all");
    scopeModal.open();
  }

  async function confirmAnalyze() {
    const scoped = scopeFrames(scopeChoice);
    if (!scoped.length) return;

    analyzing = true;

    spinners.select(
      ...scoped.map((loc) => ({
        id: loc.id,
        index: visibleIndices[loc.page_id],
        x: loc.position.x,
        y: loc.position.y,
      })),
    );

    const notifier = await analyzeOpeningClips(
      $group,
      scoped,
      $singleOpeningLlmModel,
      $singleOpeningReasoningLevel,
      $org.id,
      $fileData,
    );

    notifier.on("update", (updates) => {
      group.update(updates);
    });

    notifier.on("location-result", ({ id }) => {
      spinners.deselect(id);
    });

    notifier.on("error", () => {
      alerts.add({ type: "error", message: "Unable to analyze frames." });
      analyzing = false;
      spinners.deselect(...scoped.map((loc) => loc.id));
    });

    notifier.on("done", (result) => {
      if (result.successCount) {
        alerts.add({ type: "ok", message: `Successfully imported ${result.successCount} clips.` });
      }

      if (result.failCount) {
        alerts.add({ type: "error", message: `Failed to import ${result.failCount} clips.` });
      }

      analyzing = false;
      spinners.deselect(...scoped.map((loc) => loc.id));

      // Show what was just built. The panel no longer moves on its own, so
      // there is nothing left to hold it here — the `built` event existed only
      // to stop a finished build swapping the panel to Export mid-review.
      reviewOpen = true;
    });
  }
</script>

{#if frameCount === 0}
  <div class="fx-hint">Find frames before building them.</div>
{:else}
  <div class="fx-actions">
    <ScaleWarning {pages} {selectedPageList} {visibleIndices} action="frames can be built" />
    <!-- One button, one label. It carries no count and no scope because it
         commits to neither — the dialog it opens is where scope is chosen,
         including rebuilding frames that are already built. That is also why
         it stays enabled once everything is built: rebuild is reached through
         here, and disabling it would strand that path. The ellipsis is the
         panel's signal that a control opens a dialog rather than acting now
         (Step 4's Export Frames carries none, and acts immediately). -->
    <button class="fx-primary-button" disabled={analyzing} on:click={() => openScopeDialog()}>
      {analyzing ? "Building…" : "Build Frames…"}
    </button>

    {#if analyzing}
      <div class="fx-step3-spinner">
        <span class="fx-spinner-ring" />
        Building frames from geometry…
      </div>
    {/if}

    <!-- Above the build guidance, matching the ScaleWarning placement rule —
         a precondition is read before the thing it gates: this says the
         frames are wrong, and the guidance below invites editing them, which
         is premature on wrong frames. Strip order when several show:
         ScaleWarning (no page scale) → oversize (scale wrong) → guidance
         (education). Informational only — nothing is blocked. The button
         arms the page-level scale-from-dimension flow on purpose, not a
         per-frame one: the dialog's "Apply scale to existing frames"
         checkbox (default checked) carries the corrected scale onto the
         oversized frames, and the registration-point snap then pulls their
         overlays into place — that chain is the remedy. Same dispatch
         Step1Scale uses; the scale panel pops its waiting state off the
         tool change by itself. -->
    {#if oversizedCount > 0}
      <div class="fx-warning fx-oversize">
        <span class="fx-oversize-title">Check the frame scale.</span>
        <span>
          {oversizedCount} frame{plural(oversizedCount)}
          {oversizedCount === 1 ? "is" : "are"} larger than
          {oversizedCount === 1 ? "its" : "their"} boundary.
        </span>
        <!-- The same control users know from step 1's secondary — arming a
             tool, so no ellipsis. Shrink-wrapped via the scoped companion
             class; the shared button class stays untouched. -->
        <button
          type="button"
          class="fx-secondary-button fx-oversize-action"
          on:click={() => dispatch("pick-dimension")}>
          Set scale from dimension
        </button>
      </div>
    {/if}

    <!-- Two states, because the overlay copy describes something that does not
         exist yet until a build has run — before that it read as instructions for
         a thing the user could not see. Before: the same amber strip step 2 uses,
         restating its instruction at the point of action, since the analyzer reads
         whatever the boundary encloses. After: what a built frame is and how to
         edit it. Grids are FrameX's own objects; the mullions they line up with
         belong to the elevation underneath, so the copy never claims to edit the
         drawing. -->
    {#if builtCount > 0}
      <div class="fx-hint">
        Built frames are editable overlays — drag to position, double-click to adjust grids, void or merge
        panels, or split frames.
      </div>
    {:else}
      <div class="fx-build-warning">
        Before building, drag the corners so the entire detail is inside the frame boundary.
      </div>
    {/if}

    {#if builtCount > 0}
      <div class="fx-review">
        <button type="button" class="fx-review-head" on:click={() => (reviewOpen = !reviewOpen)}>
          <span class="fx-review-chevron" class:open={reviewOpen}>
            <CaretDownIcon />
          </span>
          <span class="fx-review-title">Review frames ({frameCount})</span>
        </button>

        {#if reviewOpen}
          <div class="fx-review-strip">
            {builtLabel(builtCount, frameCount)} — review below, or edit a frame here.
          </div>
          <div class="fx-review-list">
            {#each frames as loc (loc.id)}
              {@const item = $group.items[loc.record_id]}
              {@const hasScale = Boolean(loc.clip?.drawing_scale)}
              {@const hasDims = isFrameBuilt(item)}
              {@const dotState = !hasScale ? "no-scale" : hasDims ? "analyzed" : "unanalyzed"}
              <div class="fx-review-row">
                <span class="fx-review-dot fx-dot-{dotState}" />
                <span class="fx-review-label">{item?.mark || "—"}</span>
                <span class="fx-review-dims"
                  >{hasDims ? formatDims(item, $group.data.settings) || "—" : "—"}</span>
                <button
                  class="fx-review-edit"
                  title="Edit frame"
                  on:click={() => dispatch("edit-frame", { locId: loc.id })}>✎</button>
              </div>
            {/each}
          </div>
        {/if}
      </div>
      <!-- Outside the disclosure: reviewing is optional, continuing is not, so
           the way onward must not be hidden behind an expander. This also puts
           it where steps 1 and 2 keep theirs — last in the body, always present
           once the step has something to hand on. -->
      <button class="fx-continue" on:click={() => dispatch("continue-to-download")}>
        Continue to Export →
      </button>
    {/if}
  </div>
{/if}

<Modal
  bind:this={scopeModal}
  closeable
  closeOnOutclick
  on:confirm={confirmAnalyze}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Build", type: "confirm", style: "primary" },
  ]}>
  <div slot="title">Build Frames</div>
  <div slot="content" class="fx-scope-options">
    <label class="fx-scope-option" class:fx-scope-selected={scopeChoice === "all"}>
      <input type="radio" bind:group={scopeChoice} value="all" />
      <span>All frames on pages in FrameX ({frameCount}) — selects and builds every frame</span>
    </label>
    <label
      class="fx-scope-option"
      class:fx-scope-selected={scopeChoice === "unbuilt"}
      class:fx-scope-disabled={unbuiltCount === 0}>
      <input type="radio" bind:group={scopeChoice} value="unbuilt" disabled={unbuiltCount === 0} />
      <span>Only frames not yet built ({unbuiltCount}) — skip what's already been built</span>
    </label>
    <label
      class="fx-scope-option"
      class:fx-scope-selected={scopeChoice === "selected"}
      class:fx-scope-disabled={selectedCount === 0}>
      <input type="radio" bind:group={scopeChoice} value="selected" disabled={selectedCount === 0} />
      <span>Only the frames you've selected ({selectedCount})</span>
    </label>
  </div>
</Modal>

<style lang="scss">
  // Step 3's hint is not an aside — it is the only place the overlay's editing
  // model is explained, and in --fx-neutral italic it read as disabled text.
  // Darkened to the body tone and set upright. Scoped to this step on purpose:
  // the other steps' hints really are asides and keep the global styling.
  .fx-hint {
    font-style: normal;
    color: var(--fx-text-alt);
  }

  // The oversize strip's two-row structure, mirroring step 2's "Adjust frame
  // boundaries." lead-line pattern — the box itself is the shared .fx-warning,
  // weight is what separates the rows.
  .fx-oversize {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .fx-oversize-title {
    font-weight: 600;
  }

  // The strip's own layout override for its remedy button: shrink-wrapped
  // rather than the class's full width, with a small gap above. Scoped here
  // so the shared .fx-secondary-button is untouched.
  .fx-oversize-action {
    width: auto;
    align-self: flex-start;
    margin-top: 2px;
  }

  // The same strip as ScaleWarning's and step 2's — same box, same amber tokens —
  // so a "do this first" prompt looks the same wherever it appears.
  .fx-build-warning {
    font-size: 12px;
    padding: 6px 8px;
    border-radius: 4px;
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    color: var(--fx-amber-text);
  }

  .fx-step3-spinner {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--fx-text-secondary);
  }

  .fx-spinner-ring {
    width: 14px;
    height: 14px;
    flex: none;
    border-radius: 50%;
    border: 2px solid var(--fx-border);
    border-top-color: var(--fx-accent);
    animation: fx-spin 0.8s linear infinite;
  }

  @keyframes fx-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .fx-review {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 2px;
  }

  .fx-review-strip {
    font-size: 12px;
    color: var(--fx-accent);
    background: var(--fx-accent-tint);
    border: 1px solid var(--fx-accent-strip-border);
    border-radius: 6px;
    padding: 7px 9px;
    line-height: 1.4;
  }

  // Mirrors the "Not in FrameX" disclosure in OrphanFramesSection.
  .fx-review-head {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 2px 0;
    cursor: pointer;
    // The body tone, not --fx-neutral: this is a control you are meant to
    // click, and the caret inherits the colour through currentColor.
    color: var(--fx-text-alt);
  }

  .fx-review-chevron {
    display: inline-flex;
    flex: none;
    // The caret ships pointing down — that's the expanded state — so it's the
    // collapsed state that carries the rotation.
    transform: rotate(-90deg);
    transition: transform 0.12s ease;

    &.open {
      transform: rotate(0deg);
    }

    :global(svg) {
      display: block;
      width: 12px;
    }
  }

  .fx-review-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .fx-review-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid var(--fx-accent-strip-border);
    border-radius: 6px;
    padding: 4px 6px;
    // Roughly five rows (row 13px line + 6px padding + 4px gap). A long
    // dimension string must never widen the row into a sideways scroll —
    // .fx-review-dims' flex/min-width:0 ellipsis is what prevents that.
    max-height: 132px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .fx-review-row {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    padding: 3px 4px;
    color: var(--fx-text-alt);
    border-radius: 4px;
  }

  .fx-review-dot {
    width: 7px;
    height: 7px;
    flex: none;
    border-radius: 50%;
    background: #fff;
    border: 1px solid var(--fx-dot-unanalyzed);

    &.fx-dot-analyzed {
      background: var(--fx-accent);
      border-color: var(--fx-accent);
    }

    // No scale takes precedence over the analyzed state.
    &.fx-dot-no-scale {
      background: #fff;
      border-color: var(--fx-danger);
    }
  }

  .fx-review-label {
    flex: none;
    font-weight: 700;
  }

  .fx-review-dims {
    flex: 1;
    min-width: 0;
    color: var(--fx-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fx-review-edit {
    all: unset;
    flex: none;
    cursor: pointer;
    color: var(--fx-neutral);

    &:hover {
      color: var(--fx-accent);
    }
  }
</style>
